import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { ProIncludedPill, IncludedPill } from '@/components/ui/pro-pill';
import { ChannelIcon } from '@/components/channels/ChannelIcon';
import { useShopPath } from '@/hooks/useShopPath';
import { formatCurrency } from '@/lib/currency';
import { pricePillKind } from '@/lib/channels';
import { cn } from '@/lib/utils';

/**
 * One channel tile in the "Expand your local reach" grid on the Channels
 * page. Renders a channel built by `buildChannel` (definition merged with
 * live status and pricing).
 *
 * Every card carries an h-36 hero band — the channel's cover image when the
 * definition has one, the channel's gradient otherwise. The price pill is
 * overlaid on the hero; the footer holds the Learn-more / Manage CTA.
 *
 * The pill is decided by the channel's billing policy (`pricePillKind`):
 * `claimed` / `slotsAvailable` come from `useTier` so an allowance-eligible
 * channel reads "Free with Pro" while a slot covers it.
 */
function PricePill({ channel, claimed, slotsAvailable, hasHeroImage }) {
	const kind = pricePillKind(channel, { claimed, slotsAvailable });

	// A live channel already wears the Live badge — "Included" would be noise.
	if (kind === 'included') return channel.live ? null : <IncludedPill />;
	if (kind === 'free-with-pro') return <ProIncludedPill />;

	// White/blur overlay over a cover image; accent pill on a gradient-only hero
	// — matches the design's conditional pill (Channels.tsx).
	const pill = cn(
		'inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold text-foreground',
		hasHeroImage ? 'border-white/60 bg-white/95 shadow-sm backdrop-blur' : 'border-border bg-accent'
	);
	if (kind === 'label') return <span className={pill}>{channel.priceLabel}</span>;
	if (kind === 'price') {
		return (
			<span className={cn(pill, 'items-baseline gap-1')}>
				{formatCurrency(channel.price, channel.priceCurrency)}
				<span className="font-medium text-muted-foreground">/month</span>
			</span>
		);
	}
	return null;
}

export function ChannelCard({ channel, claimed = false, slotsAvailable = 0 }) {
	const { shopPath } = useShopPath();
	const detailHref = shopPath(`/channels/${channel.id}`);
	// A broken cover image (e.g. a 404'd definition asset) must fall back to the
	// channel gradient, not show the browser's broken-image glyph over it.
	const [coverFailed, setCoverFailed] = useState(false);
	const hasHeroImage = Boolean(channel.coverImageUrl) && !coverFailed;
	const pricePill = <PricePill channel={channel} claimed={claimed} slotsAvailable={slotsAvailable} hasHeroImage={hasHeroImage} />;

	return (
		<SoftCard
			className={cn(
				'relative flex h-full flex-col overflow-hidden !p-5 transition md:!p-6',
				channel.live ? 'ring-1 ring-success/30' : 'border-dashed opacity-95 hover:opacity-100'
			)}
		>
			{channel.live ? (
				<div className="absolute left-3 top-3 z-10">
					<span className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-1 text-xs font-semibold text-success-soft-foreground shadow-sm">
						<span className="relative flex h-2 w-2">
							<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
							<span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
						</span>
						Live
					</span>
				</div>
			) : null}
			{channel.tag ? (
				<div className="absolute right-3 top-3 z-10">
					<span
						className="inline-flex items-center rounded-full bg-action-soft px-2.5 py-1 text-xs font-semibold text-action-soft-foreground shadow-sm"
						title="This channel is new and still being fine-tuned. It works, but you might spot the occasional rough edge."
					>
						{channel.tag}
					</span>
				</div>
			) : null}

			<Link
				to={detailHref}
				className={cn('relative -mx-5 -mt-5 mb-4 block h-36 overflow-hidden md:-mx-6 md:-mt-6', channel.heroGradient)}
			>
				{channel.coverImageUrl && !coverFailed ? (
					<img
						src={channel.coverImageUrl}
						alt=""
						onError={() => setCoverFailed(true)}
						className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
					/>
				) : null}
				{pricePill ? <div className="absolute left-3 top-3">{pricePill}</div> : null}
			</Link>

			<div className="flex flex-1 items-start gap-4">
				<ChannelIcon channelId={channel.id} src={channel.iconUrl} className="h-11 w-11" />
				<div className="min-w-0 flex-1">
					<h3 className="text-[15px] font-bold text-foreground md:text-base">{channel.title}</h3>
					<p className="mt-1 text-sm text-muted-foreground">{channel.description}</p>
				</div>
			</div>

			<div className="mt-5 flex items-center justify-between gap-3 pt-1">
				{channel.pending ? (
					<span className="inline-flex items-center rounded-full bg-action-soft px-2.5 py-1 text-xs font-semibold text-action-soft-foreground">
						{channel.enablement === 'wizard' ? 'Setup pending' : 'Interest registered'}
					</span>
				) : (
					<span />
				)}
				{channel.live ? (
					<Link
						to={shopPath(`/channels/${channel.id}/manage`)}
						className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-accent"
					>
						Manage connection <ArrowRight className="h-3.5 w-3.5" />
					</Link>
				) : (
					<Link
						to={detailHref}
						className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition hover:opacity-90"
					>
						Learn more <ArrowRight className="h-3.5 w-3.5" />
					</Link>
				)}
			</div>
		</SoftCard>
	);
}
